import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { Store } from "../../../services/store.service";
import { ServicePropertiesControllerService } from "../../../services/service-controller/service-properties-controller.service";
import { GuideFormControllerService } from "../../../services/form-controller/guide-form-controller.service";
import { ApplicantFormControllerService } from "../../../services/form-controller/applicant-form-controller.service";
import { PaymentFormControllerService } from "../../../services/form-controller/payment-form-controller.service";
import { SendFileFormControllerService } from "../../../services/form-controller/sendfile-form-controller.service";
import { DocumentFormControllerService } from "../../../services/form-controller/document-form-controller.service";
import { TutorialBase } from "../../../models/tutorial-base";
import { GuideForm } from "../../../models/guide-form";
import { BaseForm } from "../../../models/base-form";

import { ServiceControllerService } from "../../../services/service-controller/service-controller.service";
import { Service } from "../../../models/service";
import { Context } from "../../../models/context";
import { TranslatedBaseError } from "../TranslatedBaseError";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { DeterminantControllerService } from "../../../services/registration-controller/determinant-controller.service";
import { RecoverOrphanConfig } from "../../../models/recover-orphan-config";

@Component({
  selector: "app-formscontainer",
  templateUrl: "./forms-container.html",
  providers: [ServicePropertiesControllerService,
    GuideFormControllerService,
    ApplicantFormControllerService,
    DocumentFormControllerService,
    PaymentFormControllerService,
    SendFileFormControllerService,
  ],
})
export class ApplicationFileFormsContainer extends TranslatedBaseError implements OnInit, OnDestroy {

  public compKey: string;
  @Input() public serviceId: string;
  @Input() public serviceName: string;
  @Input() public determinantMapById: { [key: string]: string } = {};
  @Input() public form: any;
  @Input() public formName: string = "";
  @Output() public activateFormEvent = new EventEmitter();
  @Output() public change = new EventEmitter();
  public activateForm() {
    if (this.form) {
      this.activateFormEvent.emit(this.form.active);
    }
  }

  public static formPaths: string[] = ["guide-form", "applicant-form", "document-form", "payment", "sendfile"];

  public oldServiceId: string = "";
  public applicationFileData: any[] = [
    {
      link: `/services/__/forms/${ApplicationFileFormsContainer.formPaths[0]}`,
      translation: "Guide",
      showActiveToggle: true,
      isActive: false,
      name: "GuideForm",
      form: null,
    },
    {
      link: `/services/__/forms/${ApplicationFileFormsContainer.formPaths[1]}`,
      translation: "Applicant form",
      showActiveToggle: true,
      isActive: false,
      name: "ApplicantForm",
      form: null,
    },
    {
      link: `/services/__/forms/${ApplicationFileFormsContainer.formPaths[2]}`,
      translation: "Documents",
      showActiveToggle: false,
      isActive: false,
      name: "DocumentForm",
      form: null,
    },
    {
      link: `/services/__/forms/${ApplicationFileFormsContainer.formPaths[3]}`,
      translation: "Payments",
      showActiveToggle: true,
      isActive: false,
      name: "PaymentForm",
      form: null,
    },
    {
      link: `/services/__/forms/${ApplicationFileFormsContainer.formPaths[4]}`,
      translation: "Send file form",
      showActiveToggle: true,
      isActive: false,
      name: "SendPageForm",
      form: null,
    },
  ];
  public showOldGuide: boolean = false;
  public guide: any;
  public guideFormId: string;
  public guideForm: any;

  public context: Context = new Context();
  public debugMode: boolean = false;
  public debugErrors: RecoverOrphanConfig[] = [];
  private debugSubscription: Subscription;
  private formBugsSubscription: Subscription;

  public formBugsDrawerOpen: boolean = false;
  public formBugsDrawerErrors: RecoverOrphanConfig[] = [];
  public formBugsDrawerFormName: string = "";

  // hack - angular does not support routerlinks properly

  constructor(private router: Router,
    private activatedRoute: ActivatedRoute,
    private serviceControllerService: ServiceControllerService,
    private store: Store,
    private guideFormControllerService: GuideFormControllerService,
    private applicantFormControllerService: ApplicantFormControllerService,
    private paymentFormControllerService: PaymentFormControllerService,
    private sendFileFormControllerService: SendFileFormControllerService,
    private documentFormControllerService: DocumentFormControllerService,
    private determinantService: DeterminantControllerService,
    toastr: ToastrService, public translateService: TranslateService) {
    super(toastr, translateService);
  }

  public ngOnInit(): void {
    this.debugSubscription = this.store.debugMode$.subscribe((mode) => {
      setTimeout(() => {
        this.debugMode = mode;
        this.fetchDebugErrors();
      });
    });

    this.store.dryRunRefresh$.subscribe(() => {
      if (!this.debugMode || !this.serviceId) { return; }
      // Silent refresh: only re-render when the error list actually changed.
      this.serviceControllerService.recoverOrphanConfig(this.serviceId).subscribe((errors) => {
        if (!this.store.dryRunErrorsEqual(this.debugErrors, errors)) {
          this.debugErrors = errors;
        }
      });
    });

    this.formBugsSubscription = this.store.formBugsDrawer$.subscribe((data) => {
      if (data) {
        this.formBugsDrawerErrors = data.errors;
        this.formBugsDrawerFormName = data.formName;
        this.formBugsDrawerOpen = true;
        this.store.state.common.dryRunDrawerOpen = false;
      }
    });

    this.activatedRoute.params.subscribe((params) => {
      this.serviceId = params.serviceId;
      this.getServiceDeterminantKeyName();
      const formIdPart = params.formId.split("#");
      // Check for component key in formId param (internal navigation) or URL fragment (external navigation)
      this.compKey = formIdPart.length > 1 ? formIdPart[1] : null;
      if (!this.compKey && window.location.hash) {
        // For direct URL access (e.g., from catalog usages), the hash fragment is separate from route params
        this.compKey = window.location.hash.substring(1); // Remove the leading '#'
      }
      this.serviceControllerService.getService(this.serviceId).subscribe(
        (data: Service) => {
          this.context.service = Object.assign(new Service(), data);
          if (!this.context.service.tutorials.serviceForms) {
            Object.assign(this.context.service.tutorials, TutorialBase.addProperty("serviceForms"));
          }
        },
      );
      if (this.serviceId !== this.oldServiceId) {
        this.applicationFileData.forEach((item) => {
          const list = item.link.split("/");
          list[2] = this.serviceId;
          item.link = list.join("/");
        });
        this.oldServiceId = this.serviceId;
        this.loadForms();
        this.fetchDebugErrors();
      }
      this.chooseSectionByUrlAndRevealIt();
    });
  }

  private fetchDebugErrors() {
    if (this.debugMode && this.serviceId) {
      this.serviceControllerService.recoverOrphanConfig(this.serviceId).subscribe((errors) => {
        this.debugErrors = errors;
      });
    } else {
      this.debugErrors = [];
    }
  }

  public getServiceDeterminantKeyName() {
    this.determinantService.getServiceDeterminantIdAndName(this.serviceId).subscribe(
      (data) => Object.assign(this.determinantMapById, data),
    );
  }

  public saveTutorial(e) {
    if (e) {
      this.context.service.tutorials.serviceForms = e;
      this.serviceControllerService.saveService(this.context.service, null).subscribe(
        (data) => {
          this.responseSuccessful("Saved successfully!");
        },
        (error) => {
          return this.showErrorMessage(error);
        },
      );
    }
  }

  private loadForms(): void {
    this.applicationFileData.forEach((item) => {
      this.loadForm(item);
    });
  }

  private loadForm(item): void {
    if (!item.form) {
      switch (item.name) {
        case "GuideForm": {
          this.guideFormControllerService.getGuideForm(this.serviceId, false).subscribe(
            (data) => {
              this.guideFormId = data.id;
              this.guideForm = data;
              item.form = Object.assign(new GuideForm(), data);
              item.form.tutorials = Object.assign(new TutorialBase().tutorials, item.form.tutorials);
              this.checkFormChange(item);
            },
          );
          break;
        }
        case "ApplicantForm": {
          this.applicantFormControllerService.getApplicantForm(this.serviceId, false).subscribe(
            (data) => {
              item.form = Object.assign(new BaseForm(), data);
              item.form.tutorials = Object.assign(new TutorialBase().tutorials, item.form.tutorials);
              this.checkFormChange(item);
            },
          );
          break;
        }
        case "DocumentForm": {
          this.documentFormControllerService.getDocumentForm(this.serviceId, false).subscribe(
            (data) => {
              item.form = Object.assign(new BaseForm(), data);
              item.form.tutorials = Object.assign(new TutorialBase().tutorials, item.form.tutorials);
              this.checkFormChange(item);
            },
          );
          break;
        }
        case "PaymentForm": {
          this.paymentFormControllerService.getPaymentForm(this.serviceId, false).subscribe(
            (data) => {
              item.form = Object.assign(new BaseForm(), data);
              item.form.tutorials = Object.assign(new TutorialBase().tutorials, item.form.tutorials);
              this.checkFormChange(item);
            },
          );
          break;
        }
        case "SendPageForm": {
          this.sendFileFormControllerService.getSendFileForm(this.serviceId, false).subscribe(
            (data) => {
              item.form = Object.assign(new BaseForm(), data);
              item.form.tutorials = Object.assign(new TutorialBase().tutorials, item.form.tutorials);
              this.checkFormChange(item);
            },
          );
          break;
        }
        default: break;
      }
    }
  }

  private checkFormChange(item): void {
    if (this.formName === item.name) {
      this.change.emit(item.form.active);
    }
  }

  public onFormReverted(updatedForm: any, index: number): void {
    this.applicationFileData[index].form = updatedForm;
  }

  public openFormBugsDrawerForForm(formId: string, formName: string): void {
    const filtered = this.debugErrors.filter((e) => e.parentId === formId);
    this.formBugsDrawerErrors = filtered;
    this.formBugsDrawerFormName = formName;
    this.formBugsDrawerOpen = true;
    this.store.state.common.dryRunDrawerOpen = false;
  }

  public closeFormBugsDrawer(): void {
    this.formBugsDrawerOpen = false;
  }

  public onFocusComponent(mistake: RecoverOrphanConfig): void {
    let formPath = "";
    switch (mistake.parentType) {
      case "GUIDEFORM": formPath = "guide-form"; break;
      case "FORM": formPath = "applicant-form"; break;
      case "DOCUMENT": formPath = "document-form"; break;
      case "PAYMENT": formPath = "payment"; break;
      case "SENDPAGE": formPath = "sendfile"; break;
    }
    if (formPath) {
      // Reset compKey to null first so Angular detects the change even if same key
      this.compKey = null;
      const formId = formPath + "#" + mistake.componentKey;
      this.router.navigate(["/services", this.serviceId, "forms", formId]);
    }
  }

  private chooseSectionByUrlAndRevealIt() {
    const url = this.router.url;
    this.applicationFileData.forEach((item) => {
      item.isActive = false;
      if (url.startsWith(item.link)) {
        this.store.state.common.userLevels.activePage = item.name;
        item.isActive = true;
      }
    });
  }

  public ngOnDestroy(): void {
    if (this.debugSubscription) {
      this.debugSubscription.unsubscribe();
    }
    if (this.formBugsSubscription) {
      this.formBugsSubscription.unsubscribe();
    }
  }
}
